/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { isExternalLink } from '@coursera-web/cds-labs';
import type { ImageType } from '@coursera-web/cds-labs';

import Imgix from 'js/components/Imgix';
import { processImage } from 'js/lib/imgix';

import type { ButtonProps } from '@coursera/cds-core';
import { Button, Hidden, Link, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { Button as EventingButton, MerchandisingModule } from '@coursera/event-pulse-types';
import type { EventData } from '@coursera/event-pulse/react';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

const CARD_MIN_HEIGHT = 275;
const TEXT_SIZE_CARD_HEIGHT_CUTOFF = 300;
const IMAGE_HIDE_CARD_HEIGHT_CUTOFF = 335;
const IMAGE_HIDE_CARD_WIDTH_CUTOFF = 225;
const BUTTON_WIDTH_POSITION_UNSET = 240;
const IMAGE_Z_INDEX = 0;

export type MobileBreakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

type BaseButton = {
  text: string;
  buttonName: EventingButton['name'];
};

export type LinkButton = {
  href: string;
  ariaLabel?: string;
} & BaseButton;

export type ActionButton = {
  onClick: () => void;
  ariaLabel?: string;
} & BaseButton;

type ImageDisplayConfig = {
  displayUnderButton?: boolean;
};

export type CampaignCardProps = {
  title: string;
  eyebrowImage?: ImageType;
  cta?: ActionButton | LinkButton;
  description?: string;
  image: ImageType;
  imageDisplayConfig?: ImageDisplayConfig;
  mobileBreakpoint: MobileBreakpoint;
  eventingData: {
    merchModule: MerchandisingModule;
    buttonName: EventingButton['name'];
  };
  forceOpenInNewTab?: boolean;
  backgroundColor?: string;
  dataTestId?: string;
};

const getDesktopBreakpoint = (mobileBreakpoint: MobileBreakpoint) => {
  switch (mobileBreakpoint) {
    case 'xs':
      return 'sm';
    case 'sm':
      return 'md';
    case 'md':
      return 'lg';
    case 'lg':
      return 'xl';
    default:
      return undefined;
  }
};

const styles = {
  card: (mobileBreakpoint: MobileBreakpoint, backgroundColor: string) => css`
    border-radius: var(--cds-spacing-200) !important;
    text-decoration: none !important;
    background-color: ${backgroundColor};
    max-height: 100%;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: end;
    justify-content: space-between;
    position: relative;
    container-type: size;

    ${breakpoints.down(mobileBreakpoint)} {
      container-type: unset;
      display: grid;
      grid-template-columns: 1fr auto;
      height: unset;
    }
  `,
  cardDesktop: css`
    min-height: ${CARD_MIN_HEIGHT}px;
  `,
  cardClickable: css`
    cursor: pointer;
    transition: 0.3s cubic-bezier(0, 0, 0.5, 1);

    &:focus {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 1px;
    }

    &:hover {
      box-shadow: 0 0 40px -8px var(--cds-color-darken-300), 0 0 24px -16px var(--cds-color-darken-300);
      transform: scale3d(1.03, 1.03, 1.08) translate3d(0.1rem, -0.25rem, 20rem);
    }
  `,
  content: (mobileBreakpoint: MobileBreakpoint) => css`
    padding: var(--cds-spacing-400) var(--cds-spacing-300);
    padding-bottom: 0;
    align-self: flex-start;

    ${breakpoints.down(mobileBreakpoint)} {
      padding: var(--cds-spacing-200);
      padding-right: var(--cds-spacing-100);

      [dir='rtl'] & {
        padding: var(--cds-spacing-200);
        padding-left: var(--cds-spacing-100);
      }
    }
  `,
  contentDesktop: css`
    /* Scale down heading text on desktop card when the card is small.
       This prevents the CTA from being pushed out of view */
    @container (max-height: ${TEXT_SIZE_CARD_HEIGHT_CUTOFF}px) {
      & > div {
        margin-bottom: var(--cds-spacing-200);
      }

      & > h2 {
        font-size: var(--cds-font-size-300);
        line-height: var(--cds-line-height-400);
      }

      & img {
        height: 14px;
      }
    }
  `,
  buttonWithImageUnder: css`
    position: absolute;
    z-index: ${IMAGE_Z_INDEX + 1};

    /* Unset button position when container width is narrow on RTL */
    @container (max-width: ${BUTTON_WIDTH_POSITION_UNSET}px) {
      [dir='rtl'] & {
        position: unset;
      }
    }
  `,
  eyebrowImage: (mobileBreakpoint: MobileBreakpoint) => css`
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down(mobileBreakpoint)} {
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  decorationImage: (imageUrl: string) => css`
    max-width: 100%;
    background-image: url(${imageUrl});
    background-position: bottom right;
    background-repeat: no-repeat;
    background-size: contain;
    width: 100%;
    height: 100%;
    z-index: ${IMAGE_Z_INDEX};

    [dir='rtl'] & {
      background-position-x: left;
    }

    /* Hide the image when the container is small, otherwise the image is comically small */
    @container (max-height: ${IMAGE_HIDE_CARD_HEIGHT_CUTOFF}px) {
      background-image: none;
    }

    /* Hide image when container width is narrow on RTL */
    @container (max-width: ${IMAGE_HIDE_CARD_WIDTH_CUTOFF}px) {
      [dir='rtl'] & {
        background-image: none;
      }
    }
  `,
  title: (mobileBreakpoint: MobileBreakpoint) => css`
    ${breakpoints.down(mobileBreakpoint)} {
      font-size: 20px;
    }
  `,
  titleMargin: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  descriptionMargin: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  mobileImageContainer: (imageUrl: string, mobileBreakpoint: MobileBreakpoint) => css`
    display: none;

    ${breakpoints.down(mobileBreakpoint)} {
      display: block;
      background-image: url(${imageUrl});
      background-position: right center;
      background-size: contain;
      background-repeat: no-repeat;
      min-width: 140px;
      width: 160px;
      height: 100%;
      align-self: stretch;

      [dir='rtl'] & {
        background-position: left center;
      }
    }
  `,
};

export const getLinkButtonProps = (
  cta: LinkButton,
  eventingData: {
    merchModule: MerchandisingModule;
    buttonName: EventingButton['name'];
  },
  forceOpenInNewTab?: boolean
) => {
  let target: string | undefined;
  let rel: string | undefined;
  const clickData: EventData<'click_merchandising_module'> = {
    button: {
      name: eventingData.buttonName,
    },
    merchandisingModule: eventingData.merchModule,
  };

  try {
    const isExternal = isExternalLink(cta.href);
    if (isExternal) {
      target = '_blank';
      rel = 'noopener noreferrer';
      clickData.button = {
        name: eventingData.buttonName,
        linkType: 'external',
        linkURL: cta.href,
      };
    } else {
      clickData.button = {
        name: eventingData.buttonName,
        linkType: 'internal',
        linkURL: cta.href,
      };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    // if the URL is relative, an error is thrown when calling isExternalLink
    target = forceOpenInNewTab ? '_blank' : undefined;
    clickData.button = {
      name: eventingData.buttonName,
      linkType: 'internal',
      linkURL: cta.href,
    };
  }

  return {
    target,
    rel,
    clickData,
  };
};

/**
 * A reusable campaign card component for promotional content.
 */
const CardTakeover = ({
  eyebrowImage,
  title,
  cta,
  description,
  image,
  imageDisplayConfig,
  mobileBreakpoint,
  eventingData,
  forceOpenInNewTab,
  backgroundColor = 'var(--cds-color-emphasis-primary-background-xxstrong)',
  dataTestId = 'campaign-card',
}: CampaignCardProps) => {
  const desktopMediaQuery = useMediaQuery(breakpoints.up(getDesktopBreakpoint(mobileBreakpoint) ?? 'xl'));
  const isDesktop = mobileBreakpoint === 'xl' ? false : desktopMediaQuery;
  const addTitleMarginBottom = (Boolean(description) || Boolean(cta)) && isDesktop;
  const addDescriptionmarginBottom = Boolean(cta) && isDesktop;
  const track = useTracker();
  const visibilityTrackerRef = useVisibilityTracker('view_merchandising_module', {
    merchandisingModule: eventingData.merchModule,
  });

  const hiddenOnMobile: Partial<React.ComponentProps<typeof Hidden>> = React.useMemo(() => {
    switch (mobileBreakpoint) {
      case 'xl':
        return {
          xlDown: true,
        };
      case 'lg':
        return {
          lgDown: true,
        };
      case 'md':
        return {
          mdDown: true,
        };
      case 'sm':
        return {
          smDown: true,
        };
      case 'xs':
        return { only: ['xs'] };
      default:
        return { only: ['xs'] };
    }
  }, [mobileBreakpoint]);

  let desktopCta: React.ReactNode;

  const buttonProps: Partial<ButtonProps> = {
    variant: 'primaryInvert',
    size: 'small',
    'aria-label': cta?.ariaLabel ?? cta?.text ?? '',
  };

  if (cta && 'href' in cta) {
    const { clickData, rel, target } = getLinkButtonProps(cta, eventingData, forceOpenInNewTab);
    const trackClick = () => track('click_merchandising_module', clickData);
    desktopCta = (
      <Button
        component="a"
        href={cta.href}
        css={imageDisplayConfig?.displayUnderButton ? styles.buttonWithImageUnder : undefined}
        onClick={(e: React.MouseEvent | React.KeyboardEvent) => {
          trackClick();
          e.stopPropagation();
        }}
        rel={rel}
        target={target}
        {...buttonProps}
      >
        {cta.text}
      </Button>
    );
  } else if (cta && 'onClick' in cta) {
    const trackClick = () => {
      track('click_merchandising_module', {
        merchandisingModule: eventingData.merchModule,
        button: {
          name: eventingData.buttonName,
        },
      });
    };
    desktopCta = (
      <Button
        onClick={(e: React.MouseEvent | React.KeyboardEvent) => {
          trackClick();
          cta.onClick();
          e.stopPropagation();
        }}
        css={imageDisplayConfig?.displayUnderButton ? styles.buttonWithImageUnder : undefined}
        {...buttonProps}
      >
        {cta.text}
      </Button>
    );
  }

  const renderContent = () => (
    <React.Fragment>
      <div
        css={[styles.content(mobileBreakpoint), isDesktop ? styles.contentDesktop : undefined]}
        data-testid={dataTestId}
      >
        {eyebrowImage?.url && (
          <div css={styles.eyebrowImage(mobileBreakpoint)}>
            <Imgix
              src={eyebrowImage.url}
              alt={eyebrowImage.description ?? ''}
              height={eyebrowImage.height ?? undefined}
            />
          </div>
        )}
        <Typography2
          variant="titleSmall"
          component="h2"
          color="invertBody"
          css={[styles.title(mobileBreakpoint), addTitleMarginBottom ? styles.titleMargin : undefined]}
        >
          {title}
        </Typography2>
        <Hidden {...hiddenOnMobile}>
          {description && (
            <Typography2
              variant="bodyPrimary"
              component="p"
              color="invertBody"
              css={addDescriptionmarginBottom ? styles.descriptionMargin : undefined}
            >
              {description}
            </Typography2>
          )}
        </Hidden>
        <Hidden {...hiddenOnMobile}>{desktopCta}</Hidden>
      </div>

      {isDesktop && image.url && <div css={styles.decorationImage(processImage(image.url))} />}

      <div css={styles.mobileImageContainer(processImage(image.url) ?? '', mobileBreakpoint)} />
    </React.Fragment>
  );

  if (!cta) {
    return (
      <div css={[styles.card(mobileBreakpoint, backgroundColor), isDesktop ? styles.cardDesktop : undefined]}>
        {renderContent()}
      </div>
    );
  }

  if ('href' in cta) {
    const { target, rel, clickData } = getLinkButtonProps(
      cta,
      { ...eventingData, buttonName: 'merchandising_module' },
      forceOpenInNewTab
    );
    const trackClick = () => {
      track('click_merchandising_module', clickData);
    };
    return (
      <Link
        href={cta.href}
        onClick={() => {
          trackClick();
        }}
        target={target}
        rel={rel}
        css={[styles.card(mobileBreakpoint, backgroundColor), styles.cardClickable]}
        ref={visibilityTrackerRef as React.MutableRefObject<HTMLAnchorElement | null>}
      >
        {renderContent()}
      </Link>
    );
  }

  const handleClick = () => {
    cta.onClick();
    track('click_merchandising_module', {
      merchandisingModule: eventingData.merchModule,
      button: {
        name: eventingData.buttonName,
      },
    });
  };

  return (
    <div
      css={[styles.card(mobileBreakpoint, backgroundColor), styles.cardClickable]}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          handleClick();
          e.preventDefault();
        }
      }}
      role="button"
      tabIndex={0}
      ref={visibilityTrackerRef as React.MutableRefObject<HTMLDivElement | null>}
    >
      {renderContent()}
    </div>
  );
};

export default CardTakeover;
